<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import type { BattleEvent } from './types.js'
import type { BattleScene3D } from './battle3d/BattleScene.js'

const props = defineProps<{
  events: BattleEvent[]
  processedCount: number
  mode: 'raid' | 'arena'
  speedMs: number
  revision: number
}>()

const emit = defineEmits<{
  ready: []
  failed: []
}>()

const mountPoint = ref<HTMLElement | null>(null)
const displayedBlueHealth = ref(1500)
const displayedRedHealth = ref(1500)
const blueShare = computed(() => `${Math.max(0, Math.min(100, displayedBlueHealth.value / 15))}%`)
const redShare = computed(() => `${Math.max(0, Math.min(100, displayedRedHealth.value / 15))}%`)
let controller: BattleScene3D | null = null
let lastProcessed = 0
let lastRevision = props.revision
let destroyed = false
let failureReported = false

function failScene(): void {
  if (destroyed || failureReported) return
  failureReported = true
  const failedController = controller
  controller = null
  try {
    failedController?.dispose()
  } catch {
    // Даже повреждённый WebGL-контекст не должен помешать включению CSS-фолбэка.
  } finally {
    emit('failed')
  }
}

function canUseWebGL2(): boolean {
  try {
    if (!window.WebGL2RenderingContext) return false
    const probe = document.createElement('canvas')
    const context = probe.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
    })
    if (!context) return false
    context.getExtension('WEBGL_lose_context')?.loseContext()
    return true
  } catch {
    return false
  }
}

function processedEvents(): BattleEvent[] {
  return props.events.slice(0, Math.max(0, Math.min(props.processedCount, props.events.length)))
}

function syncScene(): void {
  if (!controller) return
  try {
    const count = Math.max(0, Math.min(props.processedCount, props.events.length))
    if (props.revision !== lastRevision || count < lastProcessed) {
      lastRevision = props.revision
      lastProcessed = count
      controller.reset(processedEvents())
      return
    }
    if (count <= lastProcessed) return
    const events = props.events.slice(lastProcessed, count)
    const ticks = new Set(events.map(event => event.tick))
    controller.applyEvents(events, ticks.size <= 1, props.speedMs)
    lastProcessed = count
  } catch {
    failScene()
  }
}

onMounted(async () => {
  if (!mountPoint.value || !canUseWebGL2()) {
    failScene()
    return
  }
  try {
    const { BattleScene3D: SceneController } = await import('./battle3d/BattleScene.js')
    if (destroyed || !mountPoint.value) return
    controller = new SceneController({
      container: mountPoint.value,
      mode: props.mode,
      onReady: () => {
        if (!failureReported && !destroyed) emit('ready')
      },
      onFatal: failScene,
      onBaseHealth: (side, health) => {
        if (side === 'blue') displayedBlueHealth.value = health
        else displayedRedHealth.value = health
      },
    })
    lastRevision = props.revision
    lastProcessed = Math.max(0, Math.min(props.processedCount, props.events.length))
    controller.applyEvents(processedEvents(), false, props.speedMs)
  } catch {
    failScene()
  }
})

watch(
  [() => props.events, () => props.processedCount, () => props.revision],
  syncScene,
  { flush: 'post' },
)

onBeforeUnmount(() => {
  destroyed = true
  const activeController = controller
  controller = null
  try {
    activeController?.dispose()
  } catch {
    // Размонтирование страницы не должно всплывать ошибкой повреждённого контекста.
  }
})
</script>

<template>
  <div
    class="battle-stage-3d"
    :class="`mode-${mode}`"
    role="img"
    :aria-label="mode === 'arena' ? 'Объёмная запись боя «Прорыва»' : 'Объёмная запись боя «Призыва»'"
  >
    <div ref="mountPoint" class="webgl-mount" />

    <div v-if="mode === 'arena'" class="base-hud" aria-label="Здоровье баз">
      <div class="base-status blue">
        <span>Синяя база</span>
        <b>{{ displayedBlueHealth }}</b>
        <i><em :style="{ width: blueShare }" /></i>
      </div>
      <div class="base-status red">
        <span>Красная база</span>
        <b>{{ displayedRedHealth }}</b>
        <i><em :style="{ width: redShare }" /></i>
      </div>
    </div>
    <div v-else class="raid-labels" aria-hidden="true">
      <span>Твой отряд</span>
      <span>Враги</span>
    </div>
    <div class="stage-vignette" aria-hidden="true" />
  </div>
</template>

<style scoped>
.battle-stage-3d {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border: 1px solid #343750;
  border-radius: 16px;
  background: radial-gradient(circle at 50% 34%, #30354e 0, #191e30 64%, #101421 100%);
  box-shadow: inset 0 0 34px #0709149c, 0 12px 28px #05060a55;
  pointer-events: none;
}

.webgl-mount { position: absolute; inset: 0; }
.webgl-mount :deep(.battle-webgl-canvas) { display: block; inline-size: 100%; block-size: 100%; }
.stage-vignette { position: absolute; inset: 0; box-shadow: inset 0 0 28px 8px #08091270; }

.base-hud {
  position: absolute;
  inset: 8px 9px auto;
  z-index: 3;
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

.base-status {
  display: grid;
  grid-template-columns: auto auto;
  gap: 2px 7px;
  inline-size: min(37%, 128px);
  padding: 6px 8px;
  border: 1px solid #ffffff20;
  border-radius: 8px;
  background: #090b14c9;
  box-shadow: 0 3px 12px #0009;
  backdrop-filter: blur(4px);
}

.base-status.red { text-align: end; }
.base-status span { overflow: hidden; color: #b7bed4; font-size: 9px; font-weight: 800; text-transform: uppercase; white-space: nowrap; }
.base-status b { font-size: 12px; line-height: 1; }
.base-status i { grid-column: 1 / -1; display: block; overflow: hidden; block-size: 4px; border-radius: 999px; background: #302d3b; }
.base-status em { display: block; block-size: 100%; border-radius: inherit; transition: width .18s linear; }
.base-status.blue em { background: #4b94e7; box-shadow: 0 0 8px #3479c9; }
.base-status.red em { margin-inline-start: auto; background: #e06060; box-shadow: 0 0 8px #d9524f; }

.raid-labels {
  position: absolute;
  inset: 9px 12px auto;
  z-index: 3;
  display: flex;
  justify-content: space-between;
  color: #aeb5ca;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: .08em;
  text-shadow: 0 2px 5px #000;
  text-transform: uppercase;
}

@media (min-width: 520px) {
  .base-status { padding: 7px 10px; }
  .base-status span, .raid-labels { font-size: 10px; }
  .base-status b { font-size: 13px; }
}
</style>
